import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { setAuthToken, setRefreshTokenCallback, setSessionExpiredCallback } from '../lib/api';
import { useAuthContext } from '../context/AuthContext';

/**
 * Componente que sincroniza el token de Auth0 con el API client.
 * Espera a que el token esté sincronizado antes de renderizar los children.
 * Debe estar dentro del Auth0Provider y AuthProvider.
 */
export function AuthTokenSync({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0();
  const { triggerSessionExpired } = useAuthContext();
  const [isTokenReady, setIsTokenReady] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);

  // Función para obtener y decodificar el token
  const getTokenWithExpiration = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      });
      
      // Decodificar el token para obtener la expiración
      let expirationTime: number | null = null;
      try {
        const decoded = jwtDecode<{ exp?: number }>(token);
        if (decoded.exp) {
          expirationTime = decoded.exp * 1000; // Convertir a milisegundos
        }
      } catch (decodeError) {
        console.warn('Could not decode token:', decodeError);
      }

      return { token, expirationTime };
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Función para refrescar el token (usada por api.ts)
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const { token } = await getTokenWithExpiration();
      setAuthToken(token);
      
      if (import.meta.env.DEV) {
        console.log('✅ Token refreshed successfully');
      }
      
      return token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      setAuthToken(null);
      triggerSessionExpired();
      return null;
    }
  }, [isAuthenticated, getTokenWithExpiration, triggerSessionExpired]);

  // Función para programar el refresh automático
  const scheduleTokenRefresh = useCallback((expirationTime: number | null) => {
    // Limpiar timer anterior si existe
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!expirationTime) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    // Refrescar 5 minutos antes de la expiración (300000 ms)
    const refreshTime = Math.max(0, timeUntilExpiration - 5 * 60 * 1000);

    if (refreshTime > 0) {
      refreshTimerRef.current = window.setTimeout(async () => {
        try {
          await refreshToken();
          // Programar el siguiente refresh
          const { expirationTime: newExpiration } = await getTokenWithExpiration();
          scheduleTokenRefresh(newExpiration);
        } catch (error) {
          console.error('Error in scheduled token refresh:', error);
          triggerSessionExpired();
        }
      }, refreshTime);

      if (import.meta.env.DEV) {
        const minutesUntilRefresh = Math.floor(refreshTime / 60000);
        console.log(`⏰ Token refresh scheduled in ${minutesUntilRefresh} minutes`);
      }
    }
  }, [refreshToken, getTokenWithExpiration, triggerSessionExpired]);

  useEffect(() => {
    const syncToken = async () => {
      if (isLoading) {
        // Auth0 todavía está cargando, esperar
        return;
      }

      if (isAuthenticated) {
        try {
          const { token, expirationTime } = await getTokenWithExpiration();
          setAuthToken(token);
          
          // Programar refresh automático
          scheduleTokenRefresh(expirationTime);
          
          if (import.meta.env.DEV) {
            console.log('✅ Auth token synced to API client');
          }
        } catch (error) {
          console.error('Error getting access token:', error);
          setAuthToken(null);
          triggerSessionExpired();
        }
      } else {
        // No autenticado, limpiar token y timer
        setAuthToken(null);
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      }
      
      // Marcar como listo (con o sin token)
      setIsTokenReady(true);
    };

    syncToken();
  }, [isAuthenticated, isLoading, getTokenWithExpiration, scheduleTokenRefresh, triggerSessionExpired]);

  // Configurar callbacks en api.ts cuando el componente está listo
  useEffect(() => {
    if (isTokenReady && isAuthenticated) {
      setRefreshTokenCallback(refreshToken);
      setSessionExpiredCallback(triggerSessionExpired);
    }
  }, [isTokenReady, isAuthenticated, refreshToken, triggerSessionExpired]);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Mostrar loading mientras Auth0 carga o el token se sincroniza
  if (isLoading || !isTokenReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-dark-400">Cargando...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
